import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getAllVehicles,
  getAllCategories,
  getAllBrands,
  deleteVehicle,
  type VehicleFilters,
  type VehicleSort,
} from "@/services/vehicleService";
import type { Vehicle, VehicleCategory, Brand } from "@oriental-ramirez/shared";
import { isMotorcycle, isAutomobile } from "@oriental-ramirez/shared";
import { Search, Pencil, Trash2, Bike, Car, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { toast } from "sonner";
import { queryKeys } from "@/lib/queryKeys";

const PAGE_SIZES = [10, 20, 50, 100];
const SORT_OPTIONS: { value: VehicleSort; label: string }[] = [
  { value: "name", label: "Nombre" },
  { value: "year", label: "Año" },
  { value: "salePrice", label: "Precio" },
  { value: "createdAt", label: "Fecha" },
];

function filterAndSort(
  vehicles: Vehicle[],
  filters: VehicleFilters,
  sortBy: VehicleSort,
  sortDir: "asc" | "desc"
): Vehicle[] {
  let list = [...vehicles];
  if (filters.search?.trim()) {
    const s = filters.search.toLowerCase().trim();
    list = list.filter(
      (v) =>
        v.name?.toLowerCase().includes(s) ||
        v.model?.toLowerCase().includes(s) ||
        (v.brand as Brand)?.name?.toLowerCase().includes(s)
    );
  }
  if (filters.type === "Motocicleta") {
    list = list.filter((v) => isMotorcycle(v));
  } else if (filters.type === "Carro") {
    list = list.filter((v) => isAutomobile(v));
  }
  if (filters.categoryId) {
    list = list.filter(
      (v) => (v.category as VehicleCategory)?.id === filters.categoryId || (v as { categoryId?: string }).categoryId === filters.categoryId
    );
  }
  if (filters.brandId) {
    list = list.filter(
      (v) => (v.brand as Brand)?.id === filters.brandId || (v as { brandId?: string }).brandId === filters.brandId
    );
  }
  list.sort((a, b) => {
    let va: string | number | undefined;
    let vb: string | number | undefined;
    switch (sortBy) {
      case "name":
        va = (a.name ?? a.model ?? "").toLowerCase();
        vb = (b.name ?? b.model ?? "").toLowerCase();
        return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
      case "year":
        va = a.year ?? 0;
        vb = b.year ?? 0;
        return sortDir === "asc" ? Number(va) - Number(vb) : Number(vb) - Number(va);
      case "salePrice":
        va = a.salePrice ?? a.minPrice ?? a.initPrice ?? 0;
        vb = b.salePrice ?? b.minPrice ?? b.initPrice ?? 0;
        return sortDir === "asc" ? Number(va) - Number(vb) : Number(vb) - Number(va);
      case "createdAt":
        va = (a.createdAt as Date)?.getTime?.() ?? 0;
        vb = (b.createdAt as Date)?.getTime?.() ?? 0;
        return sortDir === "asc" ? Number(va) - Number(vb) : Number(vb) - Number(va);
      default:
        return 0;
    }
  });
  return list;
}

type VehiclesProps = {
  /** Cuando se define, la página muestra solo motos o solo autos (sidebar: Motocicletas / Automóviles) */
  fixedType?: "Motocicleta" | "Carro";
};

export function Vehicles({ fixedType }: VehiclesProps = {}) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: vehicles = [], isLoading: loading } = useQuery({
    queryKey: queryKeys.vehicles,
    queryFn: getAllVehicles,
  });
  const { data: categories = [] } = useQuery({
    queryKey: queryKeys.vehicleCategories,
    queryFn: getAllCategories,
  });
  const { data: brands = [] } = useQuery({
    queryKey: queryKeys.vehicleBrands,
    queryFn: getAllBrands,
  });
  const [filters, setFilters] = useState<VehicleFilters>({
    search: searchParams.get("q") ?? "",
    type: fixedType ?? ((searchParams.get("type") as "Motocicleta" | "Carro") || undefined),
    categoryId: searchParams.get("category") || undefined,
    brandId: searchParams.get("brand") || undefined,
  });
  const [sortBy, setSortBy] = useState<VehicleSort>((searchParams.get("sort") as VehicleSort) || "createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    (searchParams.get("dir") as "asc" | "desc") || "desc"
  );
  const [pageSize, setPageSize] = useState(Number(searchParams.get("pageSize")) || 20);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);

  const filtered = useMemo(
    () => filterAndSort(vehicles, filters, sortBy, sortDir),
    [vehicles, filters, sortBy, sortDir]
  );
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize]
  );

  const motoCount = useMemo(() => vehicles.filter((v) => isMotorcycle(v)).length, [vehicles]);
  const autoCount = useMemo(() => vehicles.filter((v) => isAutomobile(v)).length, [vehicles]);

  const updateUrl = (updates: Record<string, string | number | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === undefined || v === "") next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: true });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Eliminar este vehículo?")) return;
    try {
      await deleteVehicle(id);
      toast.success("Vehículo eliminado");
      await queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const toggleSortDir = () => {
    const next = sortDir === "asc" ? "desc" : "asc";
    setSortDir(next);
    updateUrl({ dir: next });
  };

  const pageTitle = fixedType === "Motocicleta" ? "Motocicletas" : fixedType === "Carro" ? "Automóviles" : "Vehículos";
  const pageSubtitle =
    fixedType === "Motocicleta"
      ? "Lista y gestión de motocicletas"
      : fixedType === "Carro"
        ? "Lista y gestión de automóviles"
        : "Motocicletas y automóviles · Filtros, orden y paginación";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">{pageTitle}</h1>
          <p className="text-muted-foreground">{pageSubtitle}</p>
        </div>
        <div className="flex gap-2">
          {fixedType !== "Carro" && (
            <Button asChild variant={fixedType === "Motocicleta" ? "default" : "outline"}>
              <Link to="/vehicles/new?type=motorcycle" className="gap-2">
                <Bike className="h-4 w-4" />
                Nueva moto
              </Link>
            </Button>
          )}
          {fixedType !== "Motocicleta" && (
            <Button asChild variant={fixedType === "Carro" ? "default" : "outline"}>
              <Link to="/vehicles/new?type=automobile" className="gap-2">
                <Car className="h-4 w-4" />
                Nuevo auto
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className={fixedType ? "grid gap-4 sm:grid-cols-1" : "grid gap-4 sm:grid-cols-3"}>
        {!fixedType && (
          <>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Total vehículos</p>
                <p className="text-2xl font-semibold text-primary">{vehicles.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Motocicletas</p>
                <p className="text-2xl font-semibold">{motoCount}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">Automóviles</p>
                <p className="text-2xl font-semibold">{autoCount}</p>
              </CardContent>
            </Card>
          </>
        )}
        {fixedType && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">
                Total {fixedType === "Motocicleta" ? "motocicletas" : "automóviles"}
              </p>
              <p className="text-2xl font-semibold text-primary">
                {fixedType === "Motocicleta" ? motoCount : autoCount}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <Tabs defaultValue="vehicles">
        <TabsList>
          <TabsTrigger value="vehicles">Vehículos</TabsTrigger>
          <TabsTrigger value="categories">Categorías</TabsTrigger>
          <TabsTrigger value="brands">Marcas</TabsTrigger>
        </TabsList>
        <TabsContent value="vehicles" className="mt-4">
          <Card>
            <CardHeader className="space-y-4 pb-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar nombre, modelo, marca..."
                    value={filters.search ?? ""}
                    onChange={(e) => {
                      setFilters((f) => ({ ...f, search: e.target.value }));
                      setPage(1);
                      updateUrl({ q: e.target.value || undefined, page: 1 });
                    }}
                    className="pl-8"
                  />
                </div>
                {!fixedType && (
                  <Select
                    value={filters.type ?? "all"}
                    onValueChange={(v) => {
                      const next = v === "all" ? undefined : (v as "Motocicleta" | "Carro");
                      setFilters((f) => ({ ...f, type: next }));
                      setPage(1);
                      updateUrl({ type: next ?? undefined, page: 1 });
                    }}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="Motocicleta">Motos</SelectItem>
                      <SelectItem value="Carro">Autos</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Select
                  value={filters.categoryId ?? "all"}
                  onValueChange={(v) => {
                    const next = v === "all" ? undefined : v;
                    setFilters((f) => ({ ...f, categoryId: next }));
                    setPage(1);
                    updateUrl({ category: next ?? undefined, page: 1 });
                  }}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categories.filter((c) => c.id).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filters.brandId ?? "all"}
                  onValueChange={(v) => {
                    const next = v === "all" ? undefined : v;
                    setFilters((f) => ({ ...f, brandId: next }));
                    setPage(1);
                    updateUrl({ brand: next ?? undefined, page: 1 });
                  }}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Marca" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {brands.filter((b) => b.id).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={sortBy}
                  onValueChange={(v) => {
                    setSortBy(v as VehicleSort);
                    updateUrl({ sort: v });
                  }}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={toggleSortDir} title={sortDir === "asc" ? "Ascendente" : "Descendente"}>
                  {sortDir === "asc" ? "↑" : "↓"}
                </Button>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                  Mostrando {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, total)} de {total}
                </p>
                <div className="flex items-center gap-2">
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => {
                      const n = Number(v);
                      setPageSize(n);
                      setPage(1);
                      updateUrl({ pageSize: n, page: 1 });
                    }}
                  >
                    <SelectTrigger className="w-[90px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZES.map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={currentPage <= 1}
                    onClick={() => {
                      setPage((p) => p - 1);
                      updateUrl({ page: currentPage - 1 });
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm tabular-nums">
                    {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={currentPage >= totalPages}
                    onClick={() => {
                      setPage((p) => p + 1);
                      updateUrl({ page: currentPage + 1 });
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground">Cargando…</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[64px]">Imagen</TableHead>
                      {!fixedType && <TableHead>Tipo</TableHead>}
                      <TableHead>Nombre / Modelo</TableHead>
                      <TableHead>Año</TableHead>
                      <TableHead>Marca</TableHead>
                      <TableHead>Precio</TableHead>
                      <TableHead className="w-[130px]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginated.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell>
                          {(v.images?.length ?? 0) > 0 ? (
                            <img
                              src={v.images![0]}
                              alt=""
                              className="h-10 w-10 rounded object-cover border border-border"
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded border border-dashed border-muted-foreground/30 bg-muted/50 text-muted-foreground">
                              {isAutomobile(v) ? (
                                <Car className="h-5 w-5 opacity-50" />
                              ) : (
                                <Bike className="h-5 w-5 opacity-50" />
                              )}
                            </div>
                          )}
                        </TableCell>
                        {!fixedType && (
                          <TableCell>
                            <Badge variant={isAutomobile(v) ? "default" : "secondary"}>
                              {isAutomobile(v) ? "Auto" : "Moto"}
                            </Badge>
                          </TableCell>
                        )}
                        <TableCell className="font-medium">
                          <Link to={`/vehicles/${v.id}`} className="hover:text-primary hover:underline transition-colors">
                            {v.name ?? v.model ?? "—"}
                          </Link>
                        </TableCell>
                        <TableCell>{v.year ?? "—"}</TableCell>
                        <TableCell>{(v.brand as Brand)?.name ?? "—"}</TableCell>
                        <TableCell>
                          {v.salePrice ?? v.minPrice ?? v.initPrice
                            ? `${v.salePrice ?? v.minPrice ?? v.initPrice} ${v.currency ?? "USD"}`
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" asChild title="Ver detalles">
                              <Link to={`/vehicles/${v.id}`}>
                                <Eye className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button variant="ghost" size="icon" asChild title="Editar">
                              <Link to={`/vehicles/edit/${v.id}`}>
                                <Pencil className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(v.id)} title="Eliminar">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {!loading && filtered.length === 0 && (
                <p className="py-8 text-center text-muted-foreground">No hay vehículos con esos filtros</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="categories" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{c.vehicleType ?? c.type ?? "—"}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {categories.length === 0 && !loading && (
                <p className="py-8 text-center text-muted-foreground">No hay categorías</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="brands" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brands.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">{b.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {brands.length === 0 && !loading && (
                <p className="py-8 text-center text-muted-foreground">No hay marcas</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
